import {
  Box,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  HStack,
  RadioGroup,
  Radio,
  Link,
  Text,
  Button,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import { SALUTATIONS, GENDER_OPTIONS } from '../../constants/personOptions';
import PostalCodeInput from '../../components/PostalCodeInput';
import ValidatedField from '../../components/ValidatedField';
import ValidatedDateField from '../../components/ValidatedDateField';
import { useAnswerCodes } from '../../hooks';
import { SUPPORTED_PROVINCES } from '../policy-info/types';
import {
  createEmptyPriorAddress,
  MAX_PRIOR_ADDRESSES,
  type RiskAddress,
} from '../../constants/locationOptions';
import type { Insured, PriorAddress } from 'api';

interface InsuredFieldsProps {
  insured: Insured;
  riskAddress: RiskAddress;
  occupationOptions: { value: string | number; label: string }[];
  isLoading: boolean;
  isDisabled: boolean;
  isCoInsured?: boolean;
  onFieldChange: (
    field: string,
    value: string | boolean | null | number,
  ) => void;
  onConsentChange: (
    field: 'retrieve_credit' | 'privacy_consent',
    value: string,
  ) => void;
  onInsuredUpdate: (insured: Insured) => void;
  onOpenConsentModal: () => void;
}

const InsuredFields = ({
  insured,
  riskAddress,
  occupationOptions,
  isLoading,
  isDisabled,
  isCoInsured = false,
  onFieldChange,
  onConsentChange,
  onInsuredUpdate,
  onOpenConsentModal,
}: InsuredFieldsProps) => {
  const { getCode, getOptions, getHabOccupationOtherCodes } = useAnswerCodes();
  const relationToInsuredOptions = getOptions('relation_to_insured');
  const occupationOtherCodes = getHabOccupationOtherCodes();
  const showOccupationOther = occupationOtherCodes.includes(insured.occupation);

  const handleOccupationChange = (value: string | number | null) => {
    const newValue = String(value ?? '');
    const shouldClearOther = !occupationOtherCodes.includes(newValue);
    if (shouldClearOther && insured.other_occupation) {
      onInsuredUpdate({
        ...insured,
        occupation: newValue,
        other_occupation: '',
      });
    } else {
      onFieldChange('occupation', newValue);
    }
  };

  const creditYes = getCode('Yes', 'credit_consent');
  const creditNo = getCode('No', 'credit_consent');
  const privacyYes = getCode('Yes', 'privacy_consent');
  const privacyNo = getCode('No', 'privacy_consent');

  const addressChangedYes = getCode(
    'Yes',
    'has_applicant_changed_address_in_the_last_3_years',
  );
  const addressChangedNo = getCode(
    'No',
    'has_applicant_changed_address_in_the_last_3_years',
  );

  const priorAddressEnabled =
    insured.has_applicant_changed_address_in_the_last_3_years ===
    addressChangedYes;

  const handleDifferentAddressChange = (checked: boolean) => {
    const updatedInsured: Insured = {
      ...insured,
      different_mailing_address: checked,
      mailing_address: checked ? '' : riskAddress.address,
      mailing_unit_number: checked ? '' : riskAddress.unit_number || '',
      mailing_city: checked ? '' : riskAddress.city,
      mailing_province: checked ? '' : riskAddress.province,
      mailing_postal_code: checked ? '' : riskAddress.postal_code,
    };
    onInsuredUpdate(updatedInsured);
  };

  const priorAddresses = insured.prior_addresses || [];
  const totalPriorAddresses = 1 + priorAddresses.length;
  const canAddMoreAddresses =
    priorAddressEnabled && totalPriorAddresses < MAX_PRIOR_ADDRESSES;

  const handleAddPriorAddress = () => {
    if (!canAddMoreAddresses) return;
    const maxId = Math.max(0, ...priorAddresses.map((a) => a.id));
    const newAddress = createEmptyPriorAddress(maxId + 1);
    onInsuredUpdate({
      ...insured,
      prior_addresses: [...priorAddresses, newAddress],
    });
  };

  const handlePriorAddressChange = (
    index: number,
    field: keyof PriorAddress,
    value: string,
  ) => {
    const updated = [...priorAddresses];
    updated[index] = { ...updated[index], [field]: value };
    onInsuredUpdate({
      ...insured,
      prior_addresses: updated,
    });
  };

  const handleDeletePriorAddress = (index: number) => {
    const updated = priorAddresses.filter((_, i) => i !== index);
    onInsuredUpdate({
      ...insured,
      prior_addresses: updated,
    });
  };

  return (
    <>
      {/* Co-Insured: Relation to Insured */}
      {isCoInsured && (
        <Grid
          templateColumns={
            insured.relation_to_insured === '514330' ? '200px 200px' : '200px'
          }
          gap={8}
          mb={4}
          alignItems="end"
        >
          <GridItem>
            <ValidatedField
              itemPath="location"
              fieldPath="co_insured.relation_to_insured"
              entity={insured as unknown as Record<string, unknown>}
              label="Relation to Insured"
              isDisabled={isDisabled}
              labelProps={{ fontWeight: '600' }}
            >
              <Select
                value={insured.relation_to_insured || ''}
                onChange={(e) =>
                  onFieldChange('relation_to_insured', e.target.value)
                }
                variant="main"
                size="sm"
              >
                {relationToInsuredOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          </GridItem>
          {insured.relation_to_insured === '514330' && (
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                  Other Relation
                </FormLabel>
                <Input
                  value={insured.other_relation_to_insured || ''}
                  onChange={(e) =>
                    onFieldChange('other_relation_to_insured', e.target.value)
                  }
                  variant="main"
                  size="sm"
                  isDisabled={isDisabled}
                />
              </FormControl>
            </GridItem>
          )}
        </Grid>
      )}

      {/* Corporation Name - only for corporation co-insureds */}
      {isCoInsured && insured.is_corporation && (
        <Grid templateColumns={{ base: '1fr', lg: '400px' }} gap={3} mb={4}>
          <GridItem>
            <ValidatedField
              itemPath="location"
              fieldPath="co_insured.last_name"
              entity={insured as unknown as Record<string, unknown>}
              label="Corporation Name"
              isDisabled={isDisabled}
            >
              <Input
                variant="main"
                size="sm"
                value={insured.last_name || ''}
                onChange={(e) => onFieldChange('last_name', e.target.value)}
              />
            </ValidatedField>
          </GridItem>
        </Grid>
      )}

      {/* Row 1: Name fields, Gender, Date Hired, Employment Status - hidden for corporation co-insureds */}
      {!(isCoInsured && insured.is_corporation) && (
        <Grid
          templateColumns={{
            base: '1fr',
            lg: '70px 1fr 1fr 1fr 80px 140px 120px 180px',
          }}
          gap={3}
          mb={4}
        >
          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm" whiteSpace="nowrap">
                Salutation
              </FormLabel>
              <Select
                value={insured.salutation || ''}
                onChange={(e) => onFieldChange('salutation', e.target.value)}
                variant="main"
                size="sm"
                isDisabled={isDisabled}
              >
                {SALUTATIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <ValidatedField
              itemPath="location"
              fieldPath={
                isCoInsured ? 'co_insured.first_name' : 'insured.first_name'
              }
              entity={insured as unknown as Record<string, unknown>}
              label="First"
              isDisabled={isDisabled}
            >
              <Input
                variant="main"
                size="sm"
                value={insured.first_name || ''}
                onChange={(e) => onFieldChange('first_name', e.target.value)}
              />
            </ValidatedField>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Middle</FormLabel>
              <Input
                variant="main"
                size="sm"
                value={insured.middle_name || ''}
                onChange={(e) => onFieldChange('middle_name', e.target.value)}
                isDisabled={isDisabled}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <ValidatedField
              itemPath="location"
              fieldPath={
                isCoInsured ? 'co_insured.last_name' : 'insured.last_name'
              }
              entity={insured as unknown as Record<string, unknown>}
              label="Last"
              isDisabled={isDisabled}
            >
              <Input
                variant="main"
                size="sm"
                value={insured.last_name || ''}
                onChange={(e) => onFieldChange('last_name', e.target.value)}
              />
            </ValidatedField>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm">Suffix</FormLabel>
              <Input
                variant="main"
                size="sm"
                value={insured.suffix || ''}
                onChange={(e) => onFieldChange('suffix', e.target.value)}
                isDisabled={isDisabled}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel fontSize="sm" whiteSpace="nowrap">
                Insured's Gender
              </FormLabel>
              <Select
                value={insured.applicant_gender || ''}
                onChange={(e) =>
                  onFieldChange('applicant_gender', e.target.value)
                }
                variant="main"
                size="sm"
                isDisabled={isDisabled}
              >
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <DateField
              label="Date Hired"
              date={parseDateString(insured.date_hired)}
              onChange={(date: Date | null) =>
                onFieldChange('date_hired', formatDateToString(date))
              }
              isDisabled={isDisabled}
            />
          </GridItem>

          <GridItem display="flex" alignItems="flex-end" pb={1}>
            <RadioGroup
              value={insured.employment_status || ''}
              onChange={(v) => onFieldChange('employment_status', v)}
              isDisabled={isDisabled}
            >
              <HStack spacing={4}>
                <Radio value="full_time" size="sm">
                  Full Time
                </Radio>
                <Radio value="retired" size="sm">
                  Retired
                </Radio>
              </HStack>
            </RadioGroup>
          </GridItem>
        </Grid>
      )}

      {/* Row 2: Mailing Address, Phone, DOB */}
      <Grid
        templateColumns={{
          base: '1fr',
          lg:
            isCoInsured && insured.is_corporation
              ? '1fr 80px 1fr 80px 100px 130px'
              : '1fr 80px 1fr 80px 100px 130px 170px',
        }}
        gap={3}
        mb={3}
      >
        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm" whiteSpace="nowrap">
              Mailing Address
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={
                insured.different_mailing_address
                  ? insured.mailing_address || ''
                  : riskAddress.address
              }
              onChange={(e) => onFieldChange('mailing_address', e.target.value)}
              isDisabled={isDisabled || !insured.different_mailing_address}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">Unit #</FormLabel>
            <Input
              variant="main"
              size="sm"
              value={
                insured.different_mailing_address
                  ? insured.mailing_unit_number || ''
                  : riskAddress.unit_number || ''
              }
              onChange={(e) =>
                onFieldChange('mailing_unit_number', e.target.value)
              }
              placeholder="Unit #"
              isDisabled={isDisabled || !insured.different_mailing_address}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">City</FormLabel>
            <Input
              variant="main"
              size="sm"
              value={
                insured.different_mailing_address
                  ? insured.mailing_city || ''
                  : riskAddress.city
              }
              onChange={(e) => onFieldChange('mailing_city', e.target.value)}
              isDisabled={isDisabled || !insured.different_mailing_address}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm">Province</FormLabel>
            <Select
              variant="main"
              size="sm"
              value={
                insured.different_mailing_address
                  ? insured.mailing_province || ''
                  : riskAddress.province
              }
              onChange={(e) =>
                onFieldChange('mailing_province', e.target.value)
              }
              isDisabled={isDisabled || !insured.different_mailing_address}
            >
              <option value="">Select</option>
              {SUPPORTED_PROVINCES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.value}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>

        <GridItem>
          <PostalCodeInput
            value={
              insured.different_mailing_address
                ? insured.mailing_postal_code || ''
                : riskAddress.postal_code
            }
            onChange={(value) => onFieldChange('mailing_postal_code', value)}
            isDisabled={isDisabled || !insured.different_mailing_address}
          />
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel fontSize="sm" whiteSpace="nowrap">
              Home Phone
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={insured.home_phone || ''}
              onChange={(e) => onFieldChange('home_phone', e.target.value)}
              isDisabled={isDisabled}
            />
          </FormControl>
        </GridItem>

        {/* DOB hidden for corporation co-insureds */}
        {!(isCoInsured && insured.is_corporation) && (
          <GridItem>
            <ValidatedDateField
              itemPath="location"
              fieldPath={
                isCoInsured
                  ? 'co_insured.date_of_birth'
                  : 'insured.date_of_birth'
              }
              entity={insured as unknown as Record<string, unknown>}
              label="Date of Birth"
              date={parseDateString(insured.date_of_birth)}
              onChange={(date: Date | null) =>
                onFieldChange('date_of_birth', formatDateToString(date))
              }
              maxDate={new Date()}
              isDisabled={isDisabled}
            />
          </GridItem>
        )}
      </Grid>

      {/* Row 3: Occupation - hidden for corporation co-insureds */}
      {!(isCoInsured && insured.is_corporation) && (
        <Grid
          templateColumns={{
            base: '1fr',
            lg: showOccupationOther
              ? 'minmax(300px, max-content) minmax(200px, 300px)'
              : 'minmax(300px, max-content)',
          }}
          gap={3}
          mb={3}
        >
          <GridItem>
            <ValidatedField
              itemPath="location"
              fieldPath={
                isCoInsured ? 'co_insured.occupation' : 'insured.occupation'
              }
              entity={insured as unknown as Record<string, unknown>}
              label="Occupation"
              isDisabled={isDisabled}
            >
              <SelectSearch
                options={occupationOptions}
                value={insured.occupation || ''}
                onChange={handleOccupationChange}
                placeholder="Select..."
                isDisabled={isDisabled}
                isLoading={isLoading}
              />
            </ValidatedField>
          </GridItem>
          {showOccupationOther && (
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Other Occupation</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={insured.other_occupation || ''}
                  onChange={(e) =>
                    onFieldChange('other_occupation', e.target.value)
                  }
                  placeholder="Specify occupation"
                  isDisabled={isDisabled}
                />
              </FormControl>
            </GridItem>
          )}
        </Grid>
      )}

      {/* Different from Risk Address checkbox */}
      <Checkbox
        isChecked={insured.different_mailing_address || false}
        onChange={(e) => handleDifferentAddressChange(e.target.checked)}
        isDisabled={isDisabled}
        size="sm"
        colorScheme="blue"
      >
        Different from Risk Address
      </Checkbox>

      {!isCoInsured && (
        <Box mt={4}>
          <Grid templateColumns="200px auto" gap={8} alignItems="end">
            <GridItem>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                  Retrieve Credit
                </FormLabel>
                <RadioGroup
                  value={insured.retrieve_credit || ''}
                  onChange={(v) => onConsentChange('retrieve_credit', v)}
                  isDisabled={isDisabled}
                >
                  <HStack spacing={4}>
                    <Radio value={creditYes} colorScheme="blue" size="sm">
                      Yes
                    </Radio>
                    <Radio value={creditNo} colorScheme="blue" size="sm">
                      No
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                  <Text as="span">Do you agree with the </Text>
                  <Link
                    color="blue.500"
                    onClick={onOpenConsentModal}
                    cursor="pointer"
                  >
                    Consent Statement
                  </Link>
                  <Text as="span">?</Text>
                </FormLabel>
                <RadioGroup
                  value={insured.privacy_consent || ''}
                  onChange={(v) => onConsentChange('privacy_consent', v)}
                  isDisabled={isDisabled}
                >
                  <HStack spacing={4}>
                    <Radio value={privacyYes} colorScheme="blue" size="sm">
                      Yes
                    </Radio>
                    <Radio value={privacyNo} colorScheme="blue" size="sm">
                      No
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            </GridItem>
          </Grid>

          <Grid
            templateColumns="auto 1fr 120px 80px 100px"
            gap={3}
            mt={4}
            alignItems="end"
          >
            <GridItem>
              <HStack spacing={4} pb={2}>
                <Text fontSize="sm" fontWeight="600" whiteSpace="nowrap">
                  Has applicant lived here for more than 3 years?
                </Text>
                <RadioGroup
                  value={
                    insured.has_applicant_changed_address_in_the_last_3_years ||
                    addressChangedNo
                  }
                  onChange={(v) =>
                    onFieldChange(
                      'has_applicant_changed_address_in_the_last_3_years',
                      v,
                    )
                  }
                  isDisabled={isDisabled}
                >
                  <HStack spacing={4}>
                    <Radio
                      value={addressChangedNo}
                      colorScheme="blue"
                      size="sm"
                    >
                      Yes
                    </Radio>
                    <Radio
                      value={addressChangedYes}
                      colorScheme="blue"
                      size="sm"
                    >
                      No
                    </Radio>
                  </HStack>
                </RadioGroup>
              </HStack>
            </GridItem>
          </Grid>

          {/* First Prior Address (mandatory when enabled) */}
          {priorAddressEnabled && (
            <Text fontSize="xs" fontWeight="600" color="gray.600" mt={3} mb={1}>
              Previous Address 1
            </Text>
          )}
          <Grid
            templateColumns="1fr 80px 120px 80px 100px"
            gap={3}
            alignItems="end"
          >
            <GridItem>
              <ValidatedField
                itemPath="location"
                fieldPath="insured.prior_address"
                entity={insured as unknown as Record<string, unknown>}
                label="Address"
                isDisabled={isDisabled || !priorAddressEnabled}
                labelProps={{
                  color: priorAddressEnabled ? undefined : 'gray.400',
                }}
              >
                <Input
                  value={insured.prior_address || ''}
                  onChange={(e) =>
                    onFieldChange('prior_address', e.target.value)
                  }
                  variant="main"
                  size="sm"
                  bg={!priorAddressEnabled ? 'gray.50' : undefined}
                />
              </ValidatedField>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="500"
                  mb={1}
                  color={priorAddressEnabled ? undefined : 'gray.400'}
                >
                  Unit #
                </FormLabel>
                <Input
                  value={insured.prior_unit_number || ''}
                  onChange={(e) =>
                    onFieldChange('prior_unit_number', e.target.value)
                  }
                  variant="main"
                  size="sm"
                  placeholder="Unit #"
                  bg={!priorAddressEnabled ? 'gray.50' : undefined}
                  isDisabled={isDisabled || !priorAddressEnabled}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <ValidatedField
                itemPath="location"
                fieldPath="insured.prior_city"
                entity={insured as unknown as Record<string, unknown>}
                label="City"
                isDisabled={isDisabled || !priorAddressEnabled}
                labelProps={{
                  color: priorAddressEnabled ? undefined : 'gray.400',
                }}
              >
                <Input
                  value={insured.prior_city || ''}
                  onChange={(e) => onFieldChange('prior_city', e.target.value)}
                  variant="main"
                  size="sm"
                  bg={!priorAddressEnabled ? 'gray.50' : undefined}
                />
              </ValidatedField>
            </GridItem>

            <GridItem>
              <ValidatedField
                itemPath="location"
                fieldPath="insured.prior_province"
                entity={insured as unknown as Record<string, unknown>}
                label="Province"
                isDisabled={isDisabled || !priorAddressEnabled}
                labelProps={{
                  color: priorAddressEnabled ? undefined : 'gray.400',
                }}
              >
                <Select
                  value={insured.prior_province || ''}
                  onChange={(e) =>
                    onFieldChange('prior_province', e.target.value)
                  }
                  placeholder="Select"
                  variant="main"
                  size="sm"
                  bg={!priorAddressEnabled ? 'gray.50' : undefined}
                >
                  {SUPPORTED_PROVINCES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </GridItem>

            <GridItem>
              <ValidatedField
                itemPath="location"
                fieldPath="insured.prior_postal_code"
                entity={insured as unknown as Record<string, unknown>}
                label="Postal Code"
                isDisabled={isDisabled || !priorAddressEnabled}
                labelProps={{
                  color: priorAddressEnabled ? undefined : 'gray.400',
                }}
              >
                <Input
                  value={insured.prior_postal_code || ''}
                  onChange={(e) =>
                    onFieldChange(
                      'prior_postal_code',
                      e.target.value.toUpperCase(),
                    )
                  }
                  variant="main"
                  size="sm"
                  maxLength={7}
                  placeholder="A1A 1A1"
                  bg={!priorAddressEnabled ? 'gray.50' : undefined}
                />
              </ValidatedField>
            </GridItem>
          </Grid>

          {/* Additional Prior Addresses */}
          {priorAddressEnabled &&
            priorAddresses.map((addr, index) => (
              <Box key={addr.id}>
                <Flex align="center" mt={3} mb={1}>
                  <Text fontSize="xs" fontWeight="600" color="gray.600">
                    Previous Address {index + 2}
                  </Text>
                  <IconButton
                    aria-label="Delete address"
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    ml={2}
                    onClick={() => handleDeletePriorAddress(index)}
                    isDisabled={isDisabled}
                  />
                </Flex>
                <Grid
                  templateColumns="1fr 80px 120px 80px 100px"
                  gap={3}
                  alignItems="end"
                >
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                        Address
                      </FormLabel>
                      <Input
                        value={addr.address || ''}
                        onChange={(e) =>
                          handlePriorAddressChange(
                            index,
                            'address',
                            e.target.value,
                          )
                        }
                        variant="main"
                        size="sm"
                        isDisabled={isDisabled}
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                        Unit #
                      </FormLabel>
                      <Input
                        value={addr.unit_number || ''}
                        onChange={(e) =>
                          handlePriorAddressChange(
                            index,
                            'unit_number',
                            e.target.value,
                          )
                        }
                        variant="main"
                        size="sm"
                        placeholder="Unit #"
                        isDisabled={isDisabled}
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                        City
                      </FormLabel>
                      <Input
                        value={addr.city || ''}
                        onChange={(e) =>
                          handlePriorAddressChange(
                            index,
                            'city',
                            e.target.value,
                          )
                        }
                        variant="main"
                        size="sm"
                        isDisabled={isDisabled}
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                        Province
                      </FormLabel>
                      <Select
                        value={addr.province || ''}
                        onChange={(e) =>
                          handlePriorAddressChange(
                            index,
                            'province',
                            e.target.value,
                          )
                        }
                        placeholder="Select"
                        variant="main"
                        size="sm"
                        isDisabled={isDisabled}
                      >
                        {SUPPORTED_PROVINCES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.value}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                        Postal Code
                      </FormLabel>
                      <Input
                        value={addr.postal_code || ''}
                        onChange={(e) =>
                          handlePriorAddressChange(
                            index,
                            'postal_code',
                            e.target.value.toUpperCase(),
                          )
                        }
                        variant="main"
                        size="sm"
                        maxLength={7}
                        placeholder="A1A 1A1"
                        isDisabled={isDisabled}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            ))}

          {/* Add Previous Address Button */}
          {canAddMoreAddresses && (
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              mt={3}
              onClick={handleAddPriorAddress}
              isDisabled={isDisabled}
            >
              Add Previous Address
            </Button>
          )}
        </Box>
      )}

      {/* Credit Consent Row - For Co-Insureds */}
      {isCoInsured && (
        <Grid templateColumns="200px auto" gap={8} mt={4} alignItems="end">
          <GridItem>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                Retrieve Credit
              </FormLabel>
              <RadioGroup
                value={insured.retrieve_credit || ''}
                onChange={(v) => onConsentChange('retrieve_credit', v)}
                isDisabled={isDisabled}
              >
                <HStack spacing={4}>
                  <Radio value={creditYes} colorScheme="blue" size="sm">
                    Yes
                  </Radio>
                  <Radio value={creditNo} colorScheme="blue" size="sm">
                    No
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                <Text as="span">Do you agree with the </Text>
                <Link
                  color="blue.500"
                  onClick={onOpenConsentModal}
                  cursor="pointer"
                >
                  Consent Statement
                </Link>
                <Text as="span">?</Text>
              </FormLabel>
              <RadioGroup
                value={insured.privacy_consent || ''}
                onChange={(v) => onConsentChange('privacy_consent', v)}
                isDisabled={isDisabled}
              >
                <HStack spacing={4}>
                  <Radio value={privacyYes} colorScheme="blue" size="sm">
                    Yes
                  </Radio>
                  <Radio value={privacyNo} colorScheme="blue" size="sm">
                    No
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
          </GridItem>
        </Grid>
      )}
    </>
  );
};

export default InsuredFields;
